import type { Ledger } from "./SiteHeader.inert";
import { PANEL_CONTROL, tabStops, trackFocusWithin } from "./SiteHeader.focus";
import { giveBack, write } from "./SiteHeader.inert";
import { createPanel } from "./SiteHeader.panel";
import { EPanel, getHeaderStore, takePanel } from "./SiteHeader.store";

const DRAWER = "[data-fa-header-drawer]";
const TOGGLE = "[data-fa-menu-toggle]";
const PANE = "[data-fa-drawer-pane]";
const LEVEL_ONE = '[data-fa-drawer-level="1"]';
const LEVEL_TWO = '[data-fa-drawer-level="2"]';
const TRIGGER = "[data-fa-drawer-trigger]";
const BACK = "[data-fa-drawer-back]";

const CLOSE_LABEL = "data-fa-menu-close-label";

/** Returns its restore, which the header's teardown owns — listeners ride the caller's signal. */
export const initDrawer = (root: HTMLElement, signal: AbortSignal): (() => void) => {
	const drawer = root.querySelector<HTMLElement>(DRAWER);
	const toggle = root.querySelector<HTMLElement>(TOGGLE);

	if (!drawer || !toggle) return () => undefined;

	/* Two ledgers: the drawer's own state is restored on every close, the level only on teardown,
	   so a close cannot slide the level-1 pane back across the screen. */
	const state: Ledger = new Map();
	const level: Ledger = new Map();

	const openLabel = toggle.getAttribute("aria-label");
	const closeLabel = root.getAttribute(CLOSE_LABEL) ?? openLabel;

	const store = getHeaderStore(root);

	const isOpen = () => toggle.getAttribute("aria-expanded") === "true";

	/* Every control the store can hand the field to stays live, so a dialog-shaped drawer can still
	   give the header up to a peer panel. */
	const panel = createPanel({
		key: EPanel.DRAWER,
		root,
		settles: drawer,
		returnTo: () => toggle,
		isOpen,
		release: () => giveBack(state),
		keep: () => [drawer, ...root.querySelectorAll<HTMLElement>(PANEL_CONTROL)],
	});

	const close = panel.close;

	const collapse = () => {
		for (const trigger of drawer.querySelectorAll(TRIGGER))
			write(level, trigger, "aria-expanded", "false");
	};

	const activate = (pane: Element) => {
		for (const other of drawer.querySelectorAll(PANE))
			write(level, other, "inert", other === pane ? null : "");

		pane.scrollTop = 0;
	};

	const unwind = () => {
		const one = drawer.querySelector(LEVEL_ONE);

		collapse();
		if (one) activate(one);
	};

	/* The opening row is read back off `aria-expanded` rather than held in a variable, so the
	   level has one source of truth and nothing can drift from it. */
	const back = () => {
		const opener = drawer.querySelector<HTMLElement>(`${TRIGGER}[aria-expanded="true"]`);

		unwind();
		opener?.focus();
	};

	const enter = (trigger: Element) => {
		const controls = trigger.getAttribute("aria-controls");
		const pane = [...drawer.querySelectorAll(PANE)].find((node) => node.id === controls);

		if (!pane) return;

		collapse();
		write(level, trigger, "aria-expanded", "true");
		activate(pane);

		// The trigger's own pane is inert and off-screen by now, so focus cannot stay on it.
		pane.querySelector<HTMLElement>(BACK)?.focus();
	};

	const open = () => {
		// Before the drawer is visible: the lock measures the gutter, and a fixed pane forces layout.
		panel.hold();

		void takePanel(store, EPanel.DRAWER, {
			claim: () => {
				write(state, toggle, "aria-expanded", "true");
				if (closeLabel !== null) write(state, toggle, "aria-label", closeLabel);

				// Also the reset for a close at level 2, which leaves the level where it was.
				unwind();
			},
			stillWanted: isOpen,
		});
	};

	/* `xl:hidden` takes the drawer off screen without releasing the field, so a widened window
	   would leave the page inert behind a pane nobody can see. */
	const heldFocus = trackFocusWithin(root, signal);
	const desktop = window.matchMedia("(width >= 80rem)");

	desktop.addEventListener(
		"change",
		(event) => {
			if (!event.matches || !isOpen()) return;

			const held = heldFocus();

			close(false);

			/* Not the toggle: it is `xl:hidden`, so the control this drawer belongs to no longer
			   exists at the width we have just reached. The nearest peer that does keeps the user
			   in the header rather than dropping them at the top of the document. */
			if (held)
				tabStops(root)
					.find((stop) => stop.closest(PANEL_CONTROL) !== null)
					?.focus();
		},
		{ signal },
	);

	toggle.addEventListener(
		"click",
		() => {
			/* `false`: the click already focused the toggle, and a programmatic re-focus is what
			   makes `:focus-visible` match, leaving a ring behind after a tap. */
			if (isOpen()) close(false);
			else open();
		},
		{ signal },
	);

	/* Nothing follows the drawer in the markup, so its last stop's Tab leaves for the browser. */
	document.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Tab" || !isOpen()) return;

			const stops = tabStops(root);
			const first = stops[0];
			const last = stops.at(-1);

			if (first === undefined || last === undefined) return;

			if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			} else if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			}
		},
		{ signal },
	);

	/* On the document, not the drawer: the toggle sits outside it and the page behind is inert,
	   so a keypress can land anywhere in the header. */
	document.addEventListener(
		"keydown",
		(event) => {
			if (event.key !== "Escape" || !isOpen()) return;

			// Escape unwinds one level, so a live level-2 pane owns the press before the drawer does.
			if (drawer.querySelector(`${LEVEL_TWO}:not([inert])`)) {
				back();
				return;
			}

			close(true);
		},
		{ signal },
	);

	/* Delegated, so a pane added by the CMS needs no re-init. */
	drawer.addEventListener(
		"click",
		(event) => {
			if (!(event.target instanceof Element)) return;

			const row = event.target.closest(TRIGGER);

			if (row) {
				enter(row);
				return;
			}

			if (event.target.closest(BACK)) back();
		},
		{ signal },
	);

	return () => {
		panel.dispose();
		giveBack(level);
	};
};
